import { Link } from "wouter";
import { 
  Scale, 
  Camera, 
  Shield, 
  Clock, 
  FileText, 
  BarChart3,
  CheckCircle,
  Lock,
  Monitor,
  Zap,
  Users,
  Bell,
  CalendarCheck,
  Eye,
  ArrowRight
} from "lucide-react";

function Header() {
  return (
    <header className="sticky top-0 z-50 w-full border-b border-[#30363D] bg-[#0D1117]/95 backdrop-blur">
      <div className="container mx-auto flex h-16 items-center justify-between px-4 md:px-6">
        <div className="flex items-center gap-2">
          <span className="text-xl font-bold text-[#06B6D4]">NAVIG</span>
          <span className="text-xl font-bold text-[#F97316]">8</span>
          <span className="hidden sm:inline-block text-sm text-[#8B949E] ml-2 border-l border-[#30363D] pl-2">
            SOFTWARE SOLUTIONS
          </span>
          <span className="hidden md:inline-block text-xs text-[#F97316] ml-1">AI in Motion</span>
        </div>
        
        <nav className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-sm text-[#8B949E] hover:text-white transition-colors" data-testid="link-features">
            Features
          </a>
          <a href="#benefits" className="text-sm text-[#8B949E] hover:text-white transition-colors" data-testid="link-benefits">
            Benefits
          </a>
          <a href="#technology" className="text-sm text-[#8B949E] hover:text-white transition-colors" data-testid="link-technology">
            Technology
          </a>
        </nav>

        <Link href="/login">
          <button className="landing-btn-primary text-sm" data-testid="button-signin">
            Sign In
          </button>
        </Link>
      </div>
    </header>
  );
}

function HeroSection() {
  return (
    <section className="py-20 md:py-32 text-center">
      <div className="container mx-auto px-4 md:px-6">
        <div className="landing-badge inline-block mb-6" data-testid="badge-powered-by-ai">
          Powered by AI & Advanced Analytics
        </div>
        
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
          <span className="text-white">Weigh</span>
          <span className="text-[#F97316]">8</span>
          <span className="text-[#06B6D4] italic"> Weighbridge</span>
          <br />
          <span className="text-[#06B6D4] italic">Management</span>
        </h1>
        
        <p className="text-[#8B949E] text-lg md:text-xl max-w-2xl mx-auto mb-10" data-testid="text-hero-description">
          Enterprise weighbridge operations reimagined. Streamline operations through
          automated weighing, AI-powered ANPR, and intelligent compliance tracking.
        </p>
        
        <div className="flex flex-wrap justify-center gap-4">
          <Link href="/login">
            <button className="landing-btn-primary flex items-center gap-2" data-testid="button-get-started">
              Get Started <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
          <button className="landing-btn-outline" data-testid="button-book-demo">
            Book a Demo
          </button>
        </div>
      </div>
    </section>
  );
}

function ProblemsSection() {
  const problems = [
    {
      icon: Lock,
      title: "Manual Data Entry Creates Errors",
      solution: "Automated ANPR captures vehicle details instantly with 99.9% accuracy"
    },
    {
      icon: Monitor,
      title: "Hours Wasted on Manual Weighing",
      solution: "AI-powered automation processes vehicles in under 90 seconds"
    },
    {
      icon: Shield,
      title: "Unauthorized Access Eating Into Margins",
      solution: "Smart access control prevents unauthorized vehicles from entering"
    },
    {
      icon: FileText,
      title: "Compliance Tracking & Slow Reporting",
      solution: "Real-time compliance monitoring with instant automated reports"
    }
  ];

  return (
    <section id="benefits" className="py-20 bg-[#0D1117]">
      <div className="container mx-auto px-4 md:px-6">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
          The Weighbridge Industry Has Problems.
          <br />
          We Have Solutions.
        </h2>
        
        <p className="text-[#8B949E] text-center max-w-2xl mx-auto mb-12">
          Traditional weighbridge operations waste time, money, and expose your margins.
          Weigh8 fixes that.
        </p>
        
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {problems.map((problem, index) => (
            <div key={index} className="landing-card" data-testid={`card-problem-${index}`}>
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-[#0D1117]">
                  <problem.icon className="w-6 h-6 text-[#F97316]" />
                </div>
                <div>
                  <h3 className="text-[#F97316] font-semibold mb-2">{problem.title}</h3>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#06B6D4] mt-0.5 flex-shrink-0" />
                    <p className="text-[#8B949E] text-sm">{problem.solution}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StepsSection() {
  const steps = [
    {
      number: "1",
      title: "ANPR Capture",
      description: "AI-powered cameras capture vehicle registration, validate bookings, and check compliance status. Boom gates open automatically for authorized vehicles.",
      badge: "Automated by default"
    },
    {
      number: "2",
      title: "Automated Weighing",
      description: "Vehicle moves to weighbridge. System automatically captures weight, links to vehicle data, and generates compliance documentation—all without manual intervention.",
      badge: "AI-powered automation"
    },
    {
      number: "3",
      title: "Exit & Documentation",
      description: "System finalizes weighbridge ticket, updates inventory records, and sends digital documentation. Vehicle exits seamlessly while all data is archived for compliance.",
      badge: "Full transparency"
    }
  ];

  return (
    <section id="technology" className="py-20 bg-[#0D1117]">
      <div className="container mx-auto px-4 md:px-6">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
          From Entry to Exit in 3 Steps
        </h2>
        
        <p className="text-[#8B949E] text-center mb-12">
          Process vehicles faster than ever before
        </p>
        
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {steps.map((step, index) => (
            <div key={index} className="landing-card text-center" data-testid={`card-step-${index}`}>
              <div className="w-12 h-12 rounded-full border-2 border-[#06B6D4] text-[#06B6D4] text-xl font-bold flex items-center justify-center mx-auto mb-4">
                {step.number}
              </div>
              <h3 className="text-white font-semibold text-lg mb-3">{step.title}</h3>
              <p className="text-[#8B949E] text-sm mb-4">{step.description}</p>
              <span className="landing-badge text-xs">{step.badge}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const features = [
    {
      icon: Camera,
      badge: "3x faster processing",
      badgeColor: "orange",
      title: "Smart ANPR System",
      description: "Capture and validate vehicles instantly with AI.",
      items: [
        "AI-powered plate recognition",
        "Real-time booking validation",
        "Automated boom gate control",
        "Instant compliance checks"
      ]
    },
    {
      icon: Scale,
      badge: "90% less errors",
      badgeColor: "orange",
      title: "Automated Weighing",
      description: "Eliminate manual processes and human error.",
      items: [
        "Automatic weight capture",
        "Multi-weighbridge support",
        "Digital ticket generation",
        "Tare weight management"
      ]
    },
    {
      icon: Shield,
      badge: "< 2hr average response",
      badgeColor: "cyan",
      title: "Compliance Center",
      description: "Track and manage compliance effortlessly.",
      items: [
        "Real-time violation alerts",
        "Document expiry tracking",
        "Automated notifications",
        "Audit trail management"
      ]
    },
    {
      icon: CalendarCheck,
      badge: "Real-time scheduling",
      badgeColor: "orange",
      title: "Smart Booking",
      description: "Manage reservations and optimize flow.",
      items: [
        "Online booking portal",
        "Capacity management",
        "Queue optimization",
        "SMS/Email confirmations"
      ]
    },
    {
      icon: BarChart3,
      badge: "Instant insights",
      badgeColor: "orange",
      title: "Analytics Dashboard",
      description: "Make data-driven decisions with real-time insights.",
      items: [
        "Live operations monitoring",
        "Customizable reports",
        "Performance metrics",
        "Scheduled report delivery"
      ]
    },
    {
      icon: Eye,
      badge: "24/7 monitoring",
      badgeColor: "cyan",
      title: "CCTV Integration",
      description: "Complete visibility across your site.",
      items: [
        "Multi-camera support",
        "Video evidence capture",
        "Incident recording",
        "Remote monitoring access"
      ]
    }
  ];

  return (
    <section id="features" className="py-20 bg-[#0D1117]">
      <div className="container mx-auto px-4 md:px-6">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 italic">
          Everything You Need in One Platform
        </h2>
        
        <p className="text-[#8B949E] text-center mb-12">
          Six powerful modules working together to optimize your weighbridge operations
        </p>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {features.map((feature, index) => (
            <div key={index} className="landing-card" data-testid={`card-feature-${index}`}>
              <div className="p-2 rounded-lg bg-[#0D1117] inline-block mb-4">
                <feature.icon className="w-6 h-6 text-[#06B6D4]" />
              </div>
              
              <div className={`${feature.badgeColor === 'orange' ? 'landing-badge-orange' : 'landing-badge'} inline-block mb-3`}>
                {feature.badge}
              </div>
              
              <h3 className="text-white font-semibold text-lg mb-2">{feature.title}</h3>
              <p className="text-[#8B949E] text-sm mb-4">{feature.description}</p>
              
              <ul className="space-y-2">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center gap-2 text-sm text-[#8B949E]">
                    <CheckCircle className="w-4 h-4 text-[#F97316] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-20 bg-[#0D1117]">
      <div className="container mx-auto px-4 md:px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 italic">
          Ready to Transform Your Weighbridge Operations?
        </h2>
        
        <p className="text-[#8B949E] mb-8">
          Join industry leaders who trust Weigh8 for their operations
        </p>
        
        <div className="flex flex-wrap justify-center gap-4">
          <Link href="/login">
            <button className="landing-btn-primary flex items-center gap-2" data-testid="button-get-started-cta">
              Get Started Today <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
          <button className="landing-btn-outline" data-testid="button-contact-sales">
            Contact Sales
          </button>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="py-6 border-t border-[#30363D] bg-[#0D1117]">
      <div className="container mx-auto px-4 md:px-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Scale className="w-5 h-5 text-[#06B6D4]" />
          <span className="font-bold text-white">WEIGH</span>
          <span className="font-bold text-[#F97316]">8</span>
          <span className="text-[#8B949E] text-sm ml-2">| Part of the NAVIG 8 Family</span>
        </div>
        
        <p className="text-[#8B949E] text-sm" data-testid="text-copyright">
          &copy; {new Date().getFullYear()} Weigh8. All rights reserved.
        </p>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="landing-page">
      <Header />
      <main>
        <HeroSection />
        <ProblemsSection />
        <StepsSection />
        <FeaturesSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
}